import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { useRolePermission } from "../lib/permissions.js";
import { RadarLoaderLarge } from "./ui";

function FullscreenLoader() {
  return (
    <div className="min-h-screen bg-background-dark flex items-center justify-center">
      <RadarLoaderLarge />
    </div>
  );
}

/**
 * RequirePermission — declarative permission gate for /admin/* routes.
 *
 * Usage:
 *   <RequirePermission action="manage_operators">
 *     <OperatorsPage/>
 *   </RequirePermission>
 *
 * Behavior:
 *   - While auth is loading: full-screen radar.
 *   - No authenticated user: redirect to /login.
 *   - Authenticated but lacks permission: render `fallback` if provided,
 *     else <Navigate to="/admin" replace />. (RoleRoute on the /admin parent
 *     handles viewer-level lockout.)
 *
 * Permission matrix lives in `lib/permissions.js`; this component is purely
 * presentational glue.
 */
export default function RequirePermission({ action, fallback, children }) {
  const { user, loading } = useAuth();
  const allowed = useRolePermission(action);

  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed) {
    return fallback ?? <Navigate to="/admin" replace />;
  }
  return children;
}
